import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import ClaimModal from "@/components/ClaimModal";
import DomainMultiSelect from "@/components/DomainMultiSelect";
import Dropdown from "@/components/Dropdown";
import Pagination, { usePagination } from "@/components/Pagination";
import SkillCard, { getGithubOrg, getOrgAvatarUrl } from "@/components/SkillCard";
import { trackSearchSubmit } from "@/lib/analytics";
import { listSkills, mySkills, searchUsers } from "@/lib/api";
import { DOMAINS, domainLabel } from "@/lib/domains";
import { useAuth } from "@/lib/auth-context";
import { useSeoMeta } from "@/lib/useSeoMeta";
import { useDelayedFlag } from "@/lib/useDelayedFlag";
import type { FollowUser, Skill } from "@/lib/types";

type Tab = "" | "trending" | "mine" | "official" | "community";

// Trending is now a single combined ranking (downloads-dominant, ratings-refined) — the old
// separate "Top" tab was merged into it.
const PUBLIC_TABS: { id: Exclude<Tab, "mine" | "official" | "community">; label: string }[] = [
  { id: "", label: "All" },
  { id: "trending", label: "Trending" },
];

type SortOption = "default" | "most-downloaded" | "top-rated" | "newest" | "highest-score";

// Options for the shared sort control (beside the domain filter). "default" keeps the backend
// order (trending rank / relevance), so it's the neutral default — shown simply as "Sort".
const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "default", label: "Sort" },
  { value: "most-downloaded", label: "Most downloaded" },
  { value: "top-rated", label: "Top Rating" },
  { value: "newest", label: "Latest" },
  { value: "highest-score", label: "Highest security score" },
];

// Client-side sort applied to whichever tab's skill list is showing. "default" is a no-op so the
// backend ranking (e.g. Trending) is preserved.
function sortSkills(list: Skill[], sort: SortOption): Skill[] {
  if (sort === "default") return list;
  return [...list].sort((a, b) => {
    if (sort === "most-downloaded") return b.metrics.downloads - a.metrics.downloads;
    if (sort === "top-rated") return (b.rating.avg - a.rating.avg) || (b.rating.count - a.rating.count);
    if (sort === "newest") return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    if (sort === "highest-score") return (b.security_score ?? 0) - (a.security_score ?? 0);
    return 0;
  });
}

// Design-system "eyebrow" label: Space Mono, uppercase, small orange dash prefix.
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-text/45">
      <span className="h-px w-5 bg-primary" />
      {children}
    </span>
  );
}

// Single magnifying-glass glyph reused by the search field and its submit button (no duplicate SVGs).
function SearchGlyph({ className = "", size = 16 }: { className?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

const SECURITY_SCORE_OPTIONS = [
  { value: "90", label: "90+" },
  { value: "80", label: "80+" },
  { value: "70", label: "70+" },
];

// Minimum-security-score filter, shared by every browsable grid. "" (placeholder shown) = no filter.
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- disabled in the UI for now; kept to re-enable
function SecurityScoreFilter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Dropdown
      value={value}
      onChange={onChange}
      options={SECURITY_SCORE_OPTIONS}
      placeholder="Security score"
      ariaLabel="Filter by security score"
      className="w-40"
      clearable
    />
  );
}

type Publisher = {
  org: string | null;
  name: string;
  skills: Skill[];
};

function PublisherGalleryCard({ publisher, onClick }: { publisher: Publisher; onClick: () => void }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const initials = publisher.name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex flex-col items-center gap-3 rounded-xl p-6 text-center transition-all"
      style={{
        background: "var(--bg-elev)",
        border: `1px solid ${hovered ? "rgba(255,107,26,0.4)" : "var(--border)"}`,
        borderRadius: "var(--radius-lg)",
        boxShadow: hovered ? "0 12px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,107,26,0.15)" : "var(--shadow-card)",
        transform: hovered ? "translateY(-2px)" : undefined,
        transition: "border-color 0.15s, box-shadow 0.15s, transform 0.15s",
      }}
    >
      {publisher.org && !imgFailed ? (
        <img src={getOrgAvatarUrl(publisher.org)} alt={publisher.name} className="h-14 w-14 rounded-full" onError={() => setImgFailed(true)} />
      ) : (
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-lg font-bold text-white/60">{initials}</span>
      )}
      <div>
        <div className="flex items-center justify-center gap-1">
          <span className="font-semibold text-sm">{publisher.name}</span>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>
        <p className="mt-0.5 text-xs" style={{ color: "var(--text-muted)" }}>
          {publisher.skills.length} {publisher.skills.length === 1 ? "skill" : "skills"}
        </p>
      </div>
    </button>
  );
}

function OfficialView({ allSkills, selectedDomains, sort }: { allSkills: Skill[]; selectedDomains: string[]; sort: SortOption }) {
  const [params, setParams] = useSearchParams();
  const [selectedPublisher, setSelectedPublisher] = useState<Publisher | null>(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const didAuto = useRef(false);
  const publisherGridRef = useRef<HTMLDivElement>(null);

  // Official (layer 0) skills the shared search returned, narrowed by the shared domain filter.
  // When a search is active, allSkills is already the backend's semantic ranking scoped to layer 0.
  const officialSkills = useMemo(() => {
    const base = allSkills.filter((s) => s.layer === 0);
    return selectedDomains.length === 0
      ? base
      : base.filter((s) => s.domains?.some((d) => selectedDomains.includes(d)));
  }, [allSkills, selectedDomains]);

  // Group the (search + domain) filtered official skills into publishers; a publisher with no
  // matching skills drops out of the gallery entirely.
  const publishers = useMemo(() => {
    const publisherMap = new Map<string, Publisher>();
    for (const skill of officialSkills) {
      const org = getGithubOrg(skill.original_source_url);
      const key = skill.owner_name ?? org ?? "unknown";
      if (!publisherMap.has(key)) {
        publisherMap.set(key, { org, name: skill.owner_name ?? key, skills: [] });
      }
      publisherMap.get(key)!.skills.push(skill);
    }
    return Array.from(publisherMap.values()).sort((a, b) => b.skills.length - a.skills.length);
  }, [officialSkills]);

  // The open publisher's skills, re-derived from the filtered publisher list so the shared search
  // and domain filter stay applied while drilled in. Declared unconditionally (hooks can't live
  // behind the early return below).
  const openPublisherSkills = useMemo(
    () => sortSkills(selectedPublisher ? publishers.find((p) => p.name === selectedPublisher.name)?.skills ?? [] : [], sort),
    [publishers, selectedPublisher, sort],
  );
  const publisherPager = usePagination(openPublisherSkills);

  // Open / close a publisher and mirror it in the URL (?tab=official&publisher=<org|name>), so a
  // hard refresh reopens the same publisher instead of dropping back to the publisher list.
  const openPublisher = (pub: Publisher) => {
    setSelectedPublisher(pub);
    setParams({ tab: "official", publisher: pub.org ?? pub.name }, { replace: true });
  };
  const closePublisher = () => {
    setSelectedPublisher(null);
    setParams({ tab: "official" }, { replace: true });
  };

  // Deep-link support: ?publisher=<org|name> pre-opens that publisher (used by the bulk result's
  // "Back to publisher" button so it lands where the claim started).
  const publisherParam = params.get("publisher");
  useEffect(() => {
    if (didAuto.current || !publisherParam || publishers.length === 0) return;
    const match = publishers.find((p) => (p.org && p.org === publisherParam) || p.name === publisherParam);
    if (match) {
      setSelectedPublisher(match);
      didAuto.current = true;
    }
  }, [publisherParam, publishers]);

  if (selectedPublisher) {
    return (
      <div>
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={closePublisher}
              aria-label="Back to publishers"
              title="Back to publishers"
              className="group mr-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/40 text-primary transition-colors hover:border-primary hover:bg-primary/10"
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:-translate-x-0.5">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            {selectedPublisher.org ? (
              <img src={getOrgAvatarUrl(selectedPublisher.org)} alt={selectedPublisher.name} className="h-10 w-10 rounded-full" />
            ) : (
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white/60">
                {selectedPublisher.name.slice(0, 2).toUpperCase()}
              </span>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-display text-lg font-bold">{selectedPublisher.name}</h2>
                <span className="flex items-center gap-0.5 rounded-full border border-blue-500/30 bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-blue-400">
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  Official
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setClaimOpen(true)}
            className="shrink-0 rounded-[12px] bg-primary px-5 py-2.5 font-display text-sm font-semibold text-white shadow-btn transition-all duration-200 hover:bg-primary-hover hover:shadow-[0_0_24px_rgba(255,107,26,0.45)] active:scale-[0.98]"
          >
            Claim
          </button>
        </div>
        <div ref={publisherGridRef} className="mt-6 scroll-mt-24">
          {openPublisherSkills.length > 0 && (
            <div className="flex items-center justify-end">
              <Pagination
                page={publisherPager.page}
                pageSize={publisherPager.pageSize}
                total={publisherPager.total}
                onPage={publisherPager.setPage}
                onPageSize={publisherPager.setPageSize}
                showSummary
                showNav={false}
                showSteps
              />
            </div>
          )}
          {openPublisherSkills.length === 0 ? (
            <p className="mt-4 text-sm text-text/50">No skills match the current search or domain filter.</p>
          ) : (
            <>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {publisherPager.paged.map((s) => <SkillCard key={s.id} skill={s} />)}
              </div>
              <div className="mt-8">
                <Pagination
                  page={publisherPager.page}
                  pageSize={publisherPager.pageSize}
                  total={publisherPager.total}
                  onPage={(p) => { publisherPager.setPage(p); publisherGridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                />
              </div>
            </>
          )}
        </div>
        {claimOpen && selectedPublisher.skills[0] && (
          <ClaimModal slug={selectedPublisher.skills[0].slug} bulk onClose={() => setClaimOpen(false)} />
        )}
      </div>
    );
  }


  return (
    <div>
      <p className="mt-4 text-sm" style={{ color: "var(--text-muted)" }}>
        Skills curated and verified by LatentForce AI from official publisher and contributor repositories.
      </p>
      {publishers.length === 0 ? (
        <p className="mt-8 text-sm text-text/50">No official skills match the current search or domain filter.</p>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {publishers.map((pub) => <PublisherGalleryCard key={pub.name} publisher={pub} onClick={() => openPublisher(pub)} />)}
        </div>
      )}
    </div>
  );
}

function CommunityView({ allSkills, selectedDomains, sort, minScore }: { allSkills: Skill[]; selectedDomains: string[]; sort: SortOption; minScore: string }) {
  const gridRef = useRef<HTMLDivElement>(null);

  // Memoized so its reference is stable across renders — otherwise `filtered` (and thus
  // usePagination's items) would change every render and reset the page back to 1 on every
  // click, breaking page navigation.
  const communitySkills = useMemo(() => allSkills.filter((s) => s.layer === 1), [allSkills]);

  const filtered = useMemo(() => {
    let result = communitySkills;
    if (selectedDomains.length > 0) {
      // OR across selected domains — show skills in ANY of the checked domains.
      result = result.filter((s) => s.domains?.some((d) => selectedDomains.includes(d)));
    }
    if (minScore) {
      result = result.filter((s) => (s.security_score ?? -1) >= Number(minScore));
    }
    return sortSkills(result, sort);
  }, [communitySkills, selectedDomains, minScore, sort]);

  const pager = usePagination(filtered);

  if (communitySkills.length === 0) {
    return (
      <div className="mt-10 flex flex-col items-center justify-center rounded-2xl border border-dashed border-white/15 bg-white/[0.02] px-6 py-14 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/5">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-text/30">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>
        <p className="font-medium">No community skills yet</p>
        <p className="mt-1 text-sm text-text/50">Be the first to publish a skill to the community.</p>
        <Link to="/skills/new" className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90">
          Publish your first skill
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div ref={gridRef} className="mt-4 scroll-mt-24">
        <div className="flex items-center justify-end">
          {filtered.length > 0 && (
            <Pagination
              page={pager.page}
              pageSize={pager.pageSize}
              total={pager.total}
              onPage={pager.setPage}
              onPageSize={pager.setPageSize}
              showSummary
              showNav={false}
              showSteps
            />
          )}
        </div>
        {filtered.length === 0 ? (
          <p className="mt-8 text-sm text-text/50">No skills found for the selected domain.</p>
        ) : (
          <>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {pager.paged.map((s) => <SkillCard key={s.id} skill={s} />)}
            </div>
            <div className="mt-8">
              <Pagination
                page={pager.page}
                pageSize={pager.pageSize}
                total={pager.total}
                onPage={(p) => { pager.setPage(p); gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function BrowsePage() {
  useSeoMeta({
    title: "Browse Skills — SkillPatch",
    description:
      "Browse and search a trusted registry of reusable AI agent skills — filter by domain, publisher, and trending.",
    path: "/skills",
  });
  const { user, accessToken } = useAuth();
  const [urlParams, setUrlParams] = useSearchParams();
  const [skills, setSkills] = useState<Skill[]>([]);
  // One search box + one domain filter, shared across every tab (they carry over as you switch),
  // each scoped to the active tab's own list. Seeded from ?q= on mount (e.g. the nav search box
  // or the landing page's hero search bar navigate to `/skills?q=...`) so the query isn't
  // silently dropped — same one-time-read pattern as `tab`/`domain` below.
  const [search, setSearch] = useState(() => urlParams.get("q") || "");
  const [tab, setTab] = useState<Tab>((urlParams.get("tab") as Tab) || "");
  // [profiles+follows] Search-bar mode toggle: search skills (default) or people. In people mode the
  // shared box drives a live dropdown of matching users instead of filtering the skill grid.
  const [searchMode, setSearchMode] = useState<"skills" | "people">("skills");
  const [people, setPeople] = useState<FollowUser[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  // Defer the loading placeholder so a fast load goes straight to content without a flash.
  const showLoading = useDelayedFlag(loading);
  // Shared multi-select domain filter (OR semantics), applied client-side to the active tab.
  // Seeded from ?domain= on mount so a link into a specific domain (e.g. the landing page's
  // hero domain tiles) pre-filters the catalog.
  const [selectedDomains, setSelectedDomains] = useState<string[]>(() => {
    const d = urlParams.get("domain");
    return d ? [d] : [];
  });
  // Shared sort, applied client-side to the active tab's list (default keeps the backend order).
  const [sort, setSort] = useState<SortOption>("default");
  // Shared minimum-security-score filter (client-side), next to the domain filter. "" = no filter.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- setter used only by the (disabled) score filter control
  const [minScore, setMinScore] = useState("");
  const toggleDomain = (slug: string) =>
    setSelectedDomains((cur) => (cur.includes(slug) ? cur.filter((d) => d !== slug) : [...cur, slug]));
  const domainCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const d of DOMAINS) counts[d.slug] = skills.filter((s) => s.domains?.includes(d.slug)).length;
    return counts;
  }, [skills]);
  const filteredMain = useMemo(() => {
    let base = selectedDomains.length === 0
      ? skills
      : skills.filter((s) => s.domains?.some((d) => selectedDomains.includes(d)));
    if (minScore) {
      base = base.filter((s) => (s.security_score ?? -1) >= Number(minScore));
    }
    return sortSkills(base, sort);
  }, [skills, selectedDomains, minScore, sort]);
  const mainPager = usePagination(filteredMain);
  const mainGridRef = useRef<HTMLDivElement>(null);

  // Last (tab|query) pair reported to GA4 — so the re-fetches this page does on its own (tab
  // switch, token restore) don't each re-count the query the user typed once.
  const lastSearchTracked = useRef("");

  async function load(currentTab: Tab = tab, { resubmitted = false } = {}) {
    setLoading(true);
    try {
      let rows: Skill[];
      if (currentTab === "mine") {
        rows = accessToken ? await mySkills(accessToken, search || undefined) : [];
      } else if (currentTab === "community") {
        // Always scope to community (layer 1) — the shared search runs semantic within it, and the
        // domain filter is client-side, so `skills` (and its domain counts) stay community-only.
        rows = await listSkills({ search: search || undefined, layer: 1, limit: 500 }, accessToken);
      } else if (currentTab === "official") {
        // Always scope to official (layer 0); the gallery groups whatever the search returned.
        rows = await listSkills({ search: search || undefined, layer: 0, limit: 500 }, accessToken);
      } else {
        // All / Trending. A search overrides the collection ranking so it runs semantic
        // (the backend only takes the hybrid path when no collection is set).
        rows = await listSkills({ search: search || undefined, collection: search ? undefined : (currentTab || undefined), limit: 500 }, accessToken);
      }
      setSkills(rows);
      // A search actually ran — report it with the count it produced. This covers queries typed
      // here AND ones arriving as ?q= from the nav box or the landing hero (both navigate here),
      // so those don't need their own event and can't double-count this one.
      const q = search.trim();
      if (q) {
        const key = `${currentTab}|${q}`;
        if (resubmitted || key !== lastSearchTracked.current) {
          lastSearchTracked.current = key;
          trackSearchSubmit(q, rows.length);
        }
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(tab);
    // Depend on accessToken too: on a hard refresh of ?tab=mine the token restores a beat
    // later, so this re-runs the fetch once it's available instead of leaving My skills empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, accessToken]);

  // [profiles+follows] People mode — debounced live search of users for the search-bar dropdown.
  // A leading "@" scopes to username-only (matching the nav search); otherwise match name + username.
  useEffect(() => {
    if (searchMode !== "people") { setPeople([]); setPeopleOpen(false); return; }
    const raw = search.trim();
    const usernameOnly = raw.startsWith("@");
    const q = usernameOnly ? raw.slice(1).trim() : raw;
    if (!q) { setPeople([]); setPeopleOpen(false); return; }
    let cancelled = false;
    setPeopleLoading(true); setPeopleOpen(true);
    const t = setTimeout(async () => {
      try { const us = await searchUsers(q, 18, accessToken, usernameOnly); if (!cancelled) setPeople(us); }
      catch { if (!cancelled) setPeople([]); }
      finally { if (!cancelled) setPeopleLoading(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, searchMode, accessToken]);

  // Close the people dropdown on outside click.
  useEffect(() => {
    if (!peopleOpen) return;
    function onClick(e: MouseEvent) {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) setPeopleOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [peopleOpen]);

  function switchTab(next: Tab) {
    setTab(next);
    // Search text + domain selection carry over across tabs — each re-applied to the new tab's
    // own list (the effect on `tab` refetches with the current search). Persist the tab in the URL.
    setUrlParams(next ? { tab: next } : {}, { replace: true });
  }

  // Label for the shared Eyebrow in the filter row, derived from the active tab.
  const tabLabel = tab === "" ? "All" : tab === "mine" ? "My skills" : tab.charAt(0).toUpperCase() + tab.slice(1);

  // Skills / People toggle — pinned inside the field on desktop, dropped to its own row on mobile.
  const modeToggleBtns = (["skills", "people"] as const).map((m) => (
    <button
      key={m}
      type="button"
      onClick={() => setSearchMode(m)}
      className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition ${
        searchMode === m ? "bg-primary text-white" : "text-text/55 hover:text-text"
      }`}
    >
      {m}
    </button>
  ));

  return (
    <main className="mx-auto max-w-[1300px] px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
      {/* Publish moved to the navbar; the Explore header is just the title + description now. */}
      <div>
        <h1 className="font-display text-3xl font-normal tracking-tight text-text">Explore</h1>
        <p className="mt-1.5 text-sm text-text/55">
          Discover, install, and improve agent skills — every one security-scanned before it's listed.
        </p>
      </div>

      {/* Shared search — one box for every tab. Skills mode filters the grid; People mode drives a
          live dropdown of users. [profiles+follows] */}
      <div ref={searchWrapRef} className="relative mt-5">
        <form onSubmit={(e) => { e.preventDefault(); if (searchMode === "skills") load(tab, { resubmitted: true }); }} className="flex flex-col gap-3">
          <div className="flex gap-3">
            <div
              className="relative flex h-[52px] flex-1 items-center gap-2.5 rounded-xl px-4"
              style={{ background: "var(--glass)", border: "1px solid var(--border)", borderTopColor: "var(--border-light)" }}
            >
              <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
              <SearchGlyph className="shrink-0 text-text/35" />
              <input
                placeholder={
                  searchMode === "people" ? "Search people by name or @username…"
                  : tab === "mine" ? "Search your skills…"
                  : tab === "official" ? "Search official skills…"
                  : tab === "community" ? "Search community skills…"
                  : "Search by name, summary, or tag…"
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={() => { if (searchMode === "people" && search.trim()) setPeopleOpen(true); }}
                className="w-full bg-transparent text-sm outline-none placeholder:text-text/35"
              />
              {/* Toggle inside the field on desktop; on mobile it drops to its own row (below). */}
              <div className="ml-2 hidden shrink-0 items-center rounded-lg border border-[var(--border)] bg-[var(--bg-elev)] p-0.5 sm:flex">
                {modeToggleBtns}
              </div>
            </div>
            {/* Always rendered so the bar doesn't reflow when switching to People (whose results are
                live); in People mode submitting is a no-op handled by the form's onSubmit guard. */}
            <button type="submit" aria-label="Search" className="flex h-[52px] shrink-0 items-center justify-center rounded-xl bg-primary px-4 font-display text-sm font-medium text-white transition hover:opacity-90 sm:px-6">
              {/* Icon-only on mobile to save horizontal space; full label from sm up. */}
              <SearchGlyph size={18} className="sm:hidden" />
              <span className="hidden sm:inline">Search</span>
            </button>
          </div>
          {/* Mobile-only Skills / People toggle row (kept out of the cramped field). */}
          <div className="flex w-fit items-center rounded-lg border border-[var(--border)] bg-[var(--bg-elev)] p-0.5 sm:hidden">
            {modeToggleBtns}
          </div>
        </form>

        {/* People results — a dropdown attached to the search bar. */}
        {searchMode === "people" && peopleOpen && search.trim() && (
          <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg-elev)] shadow-2xl">
            <div className="max-h-[60vh] overflow-y-auto p-2">
              {peopleLoading && people.length === 0 && (
                <div className="flex items-center justify-center gap-2 px-3 py-6 text-xs text-text/45">
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-text/20 border-t-primary" />
                  Searching…
                </div>
              )}
              {!peopleLoading && people.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-text/45">No people match “{search.trim()}”.</p>
              )}
              {people.map((u) => (
                <Link key={u.id} to={`/u/${u.username}`} onClick={() => setPeopleOpen(false)}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 transition hover:bg-[var(--glass-strong)]">
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
                  ) : (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{ background: "var(--grad-emphasis)" }}>
                      {u.display_name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?"}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-text">{u.display_name}</span>
                    <span className="block truncate font-mono text-xs text-text/45">@{u.username}</span>
                  </span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text/25">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <nav className="no-scrollbar mt-6 flex gap-6 overflow-x-auto border-b border-white/10 sm:gap-7">
        {PUBLIC_TABS.map(({ id, label }) => (
          <button key={id} onClick={() => switchTab(id)} className={`relative shrink-0 whitespace-nowrap pb-3 font-display text-sm font-medium transition ${tab === id ? "text-text" : "text-text/50 hover:text-text/80"}`}>
            {label}
            {tab === id && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />}
          </button>
        ))}

        <button onClick={() => switchTab("official")} className={`relative flex shrink-0 items-center gap-1.5 whitespace-nowrap pb-3 font-display text-sm font-medium transition ${tab === "official" ? "text-text" : "text-text/50 hover:text-text/80"}`}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          Official
          {tab === "official" && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />}
        </button>

        <button onClick={() => switchTab("community")} className={`relative flex shrink-0 items-center gap-1.5 whitespace-nowrap pb-3 font-display text-sm font-medium transition ${tab === "community" ? "text-text" : "text-text/50 hover:text-text/80"}`}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-text/50">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          Community
          {tab === "community" && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />}
        </button>

        {user && (
          <button onClick={() => switchTab("mine")} className={`relative shrink-0 whitespace-nowrap pb-3 font-display text-sm font-medium transition ${tab === "mine" ? "text-text" : "text-text/50 hover:text-text/80"}`}>
            My skills
            {tab === "mine" && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />}
          </button>
        )}
      </nav>

      {/* Shared sort + domain + security-score filter — every tab, not just the main grid view.
          OfficialView/CommunityView take these as props and apply them internally, so this row
          must render regardless of which tab is active, or there'd be no way to change them
          while actually on those two tabs. */}
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <Eyebrow>{tabLabel}</Eyebrow>
        {/* On mobile Sort + All domains share one row (each 50%); `sm:contents` dissolves this
            wrapper from sm up so they flow inline in the desktop filter bar as before. */}
        <div className="flex gap-2 sm:contents">
          <Dropdown
            value={sort}
            onChange={(v) => setSort(v as SortOption)}
            ariaLabel="Sort skills"
            className="flex-1 sm:w-48 sm:flex-none"
            options={SORT_OPTIONS}
          />
          <DomainMultiSelect
            selected={selectedDomains}
            onToggle={toggleDomain}
            onClear={() => setSelectedDomains([])}
            placeholder="All domains"
            hideSelectedChips
            className="flex-1 sm:w-auto sm:flex-none"
            options={DOMAINS.map((d) => ({ slug: d.slug, label: d.label, count: domainCounts[d.slug] }))}
          />
        </div>
        {/* Selected-domain chips sit inline, right beside the domain filter. */}
        {selectedDomains.map((slug) => (
          <span
            key={slug}
            className="flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-elev)] py-1 pl-2.5 pr-1.5 text-xs font-medium text-text/80"
          >
            {domainLabel(slug)}
            <button
              type="button"
              onClick={() => toggleDomain(slug)}
              aria-label={`Remove ${domainLabel(slug)}`}
              className="flex text-text/40 transition-colors hover:text-text"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
        {/* Security-score filter disabled for now — re-enable by restoring this control.
        <SecurityScoreFilter value={minScore} onChange={setMinScore} /> */}
      </div>

      {tab === "official" && (loading ? (showLoading ? <p className="mt-8 text-text/50">Loading…</p> : null) : <OfficialView allSkills={skills} selectedDomains={selectedDomains} sort={sort} />)}
      {tab === "community" && (loading ? (showLoading ? <p className="mt-8 text-text/50">Loading…</p> : null) : <CommunityView allSkills={skills} selectedDomains={selectedDomains} sort={sort} minScore={minScore} />)}

      {tab !== "official" && tab !== "community" && (
        <>
          {loading ? (
            showLoading ? <p className="mt-8 text-text/50">Loading…</p> : null
          ) : skills.length === 0 ? (
            tab === "mine" ? (
              search ? (
                <p className="mt-8 text-text/50">No skills of yours match &ldquo;{search}&rdquo;.</p>
              ) : (
                <div className="mt-10 flex flex-col items-center justify-center rounded-2xl border border-dashed border-white/15 bg-white/[0.02] px-6 py-14 text-center">
                  <p className="text-sm text-text/50">You haven&apos;t added a skill yet.</p>
                  <Link to="/skills/new" className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90">
                    Add your first skill
                  </Link>
                </div>
              )
            ) : (
              <p className="mt-8 text-text/50">No published skills found. Be the first to add one.</p>
            )
          ) : (
            <div ref={mainGridRef} className="mt-6 scroll-mt-24">
              {filteredMain.length > 0 && (
                <div className="flex items-center justify-end">
                  <Pagination
                    page={mainPager.page}
                    pageSize={mainPager.pageSize}
                    total={mainPager.total}
                    onPage={mainPager.setPage}
                    onPageSize={mainPager.setPageSize}
                    showSummary
                    showNav={false}
                    showSteps
                  />
                </div>
              )}
              {filteredMain.length === 0 ? (
                <p className="mt-8 text-text/50">No skills found for the selected domain.</p>
              ) : (
                <>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {mainPager.paged.map((s) => <SkillCard key={s.id} skill={s} showStatus={tab === "mine"} />)}
                  </div>
                  <div className="mt-8">
                    <Pagination
                      page={mainPager.page}
                      pageSize={mainPager.pageSize}
                      total={mainPager.total}
                      onPage={(p) => { mainPager.setPage(p); mainGridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </main>
  );
}
